/**
 * ⭐ T2075 M5 (plan Step 2, D11) — THE ONE CSV CORE, shared by the server and
 * the browser bundle. Two jobs, both pure and both dependency-free so the
 * client bundle can import this module exactly as it imports
 * `access-review.ts`'s builder today:
 *
 *   · `buildCsv(rows)`   — the EXPORT side: a UTF-8 BOM, CRLF line ends and
 *                          every cell through `csvCell` (RFC 4180 quoting plus
 *                          the formula-lead neutraliser). The staff export, the
 *                          home's access review and any sheet that leaves this
 *                          system for Excel are built here (S14).
 *   · `parseCsvText(text, delimiter)` — the IMPORT side: RFC 4180 quotes, doubled
 *                          quotes, embedded newlines inside a quoted cell, and a
 *                          1-INDEXED SOURCE LINE on every record so the import
 *                          report can name the line a rejected row came from.
 *
 * ⛔ `csvCell` MOVED HERE FROM `access-review.ts` (its `:244` at the M4 base)
 * and is re-exported there by identity, so the two sheets cannot neutralise
 * differently. Its behaviour is unchanged: quote, double an embedded quote,
 * prefix a leading `= + - @ \t \r` with an apostrophe.
 *
 * ⛔ THE PARSER NEVER THROWS ON DATA. An unterminated quote at end of file closes
 * the cell rather than raising: the caller sees a row with the text it could
 * read and the server validates every cell anyway (D11), so a malformed file
 * degrades to rejected lines in the report, never to a blank screen.
 *
 * ⛔ NO DELIMITER SNIFFING HERE. The browser sniffs the header (`csv-import.ts`)
 * because it holds the decoded text and the report names the delimiter it
 * chose; this function takes the answer as a parameter so the server side, if
 * it ever parses, cannot sniff differently.
 */

/** Leading characters Excel/Sheets treat as the start of a formula. */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/**
 * RFC4180-ish quoting: wrap in quotes and double any embedded quote. Home
 * names carry commas and apostrophes ("St. Colman's Nursing Home"), and an
 * access list that silently splits a column is a wrong access list.
 *
 * ⛔ QUOTING AND FORMULA-NEUTRALISING ARE TWO DIFFERENT JOBS, and the quoting
 * only does the first. Wrapping in quotes makes a parser read the cell
 * correctly; it does NOT stop Excel treating a cell that BEGINS `= + - @`
 * (or tab / CR) as a formula. These sheets leave the organisation — to a
 * nursing-home administrator who opens them in Excel, the one population most
 * likely to click "Enable content". A leading apostrophe is the standard
 * neutraliser and is invisible in the rendered cell.
 *
 * ⛔ REACHABILITY: `opsCreateNurse`, `opsCreateHome` and, since T2075 M5, the
 * admin door's staff writers and the CSV import all write names from request
 * bodies into the columns this function renders, so the neutraliser is
 * LOAD-BEARING rather than anticipatory. `staff-export.spec.ts` grades a name
 * that begins `=` (S14).
 */
export function csvCell(value: string): string {
  const neutralised = FORMULA_LEAD.test(value) ? `'${value}` : value;
  return `"${neutralised.replace(/"/g, '""')}"`;
}

/** The byte order mark Excel needs to read UTF-8 (a fada) as UTF-8. */
export const CSV_BOM = '\uFEFF';

/** The cap on rows per staff import (plan D11, S13): the browser refuses a
 *  longer file with the count BEFORE posting, and `importStaff` checks
 *  `rows.length` before its loop. Declared here, dependency-free, so both
 *  halves read one figure; `staff-repo.ts` re-exports it. */
export const STAFF_IMPORT_MAX_ROWS = 500;

/**
 * A whole sheet: BOM, then every row's cells through `csvCell` joined by `,`,
 * rows joined by CRLF, with a trailing CRLF. `rows[0]` is the header by
 * convention; this function treats every row alike.
 */
export function buildCsv(rows: readonly (readonly string[])[]): string {
  return CSV_BOM + rows.map((row) => row.map(csvCell).join(',')).join('\r\n') + '\r\n';
}

export type CsvRecord = {
  /** 1-indexed line of the SOURCE text the record STARTED on, for the report. */
  line: number;
  cells: string[];
};

/**
 * RFC 4180 over already-decoded text. `delimiter` is one character (`,`, `;`
 * or a tab). A quoted cell may hold the delimiter, a doubled quote (`""` → `"`)
 * and line breaks; an unquoted cell ends at the delimiter or the line end.
 * `\r\n`, `\n` and a lone `\r` all end a record. A BOM at position 0 is
 * dropped. A record whose every cell is empty is still returned (the caller
 * decides what a blank row means; the import reports it as rejected by line).
 * The trailing line end produces no extra record.
 */
export function parseCsvText(text: string, delimiter: string): CsvRecord[] {
  const src = text.startsWith(CSV_BOM) ? text.slice(1) : text;
  const records: CsvRecord[] = [];
  let cells: string[] = [];
  let cell = '';
  let quoted = false;
  let line = 1;
  let recordLine = 1;
  let sawAnyChar = false;
  let i = 0;
  const n = src.length;

  const endRecord = (): void => {
    cells.push(cell);
    records.push({ line: recordLine, cells });
    cells = [];
    cell = '';
    sawAnyChar = false;
  };

  while (i < n) {
    const c = src[i]!;
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        quoted = false;
        i += 1;
        continue;
      }
      if (c === '\r' && src[i + 1] === '\n') {
        cell += '\n';
        line += 1;
        i += 2;
        continue;
      }
      if (c === '\n' || c === '\r') {
        cell += '\n';
        line += 1;
        i += 1;
        continue;
      }
      cell += c;
      i += 1;
      continue;
    }
    if (!sawAnyChar) {
      sawAnyChar = true;
      recordLine = line;
    }
    if (c === '"' && cell === '') {
      quoted = true;
      i += 1;
      continue;
    }
    if (c === delimiter) {
      cells.push(cell);
      cell = '';
      i += 1;
      continue;
    }
    if (c === '\r' && src[i + 1] === '\n') {
      endRecord();
      line += 1;
      i += 2;
      continue;
    }
    if (c === '\n' || c === '\r') {
      endRecord();
      line += 1;
      i += 1;
      continue;
    }
    cell += c;
    i += 1;
  }
  // A final record with no trailing line end (or an unterminated quote) still
  // counts; an empty tail after the last line end does not.
  if (sawAnyChar || quoted || cells.length > 0 || cell !== '') endRecord();
  return records;
}

/** ⭐ T2173 rework 1 — MOVED HERE FROM `staff-repo.ts`, so the browser's
 *  pre-import COUNT and the server's storability VERDICT are the same reading.
 *  The confirm names how many rows a file will deactivate; if it counted with a
 *  second yes/no test, the sentence could promise a number the repo would not
 *  produce. This module has no imports, which is what makes it reachable from
 *  the client bundle (`csv-import.ts` already takes `parseCsvText` from it).
 *
 *  ⛔ SHARING THE PARSER IS NOT SHARING THE DECISION (D11): the browser uses it
 *  to COUNT and to ask; `staff-repo` still calls it itself and still refuses by
 *  line. A doctored fetch that skips the browser entirely changes nothing.
 *
 *  ⭐ The Active cell → the state it asks for, or a refusal.
 *  `undefined` means "unchanged" and is the answer for an absent or blank cell.
 *  ⛔ THE VOCABULARY IS THE EXPORT'S: `adminExportStaff` writes `yes` / `no`
 *  lowercase, so an untouched export round-trips, and an admin who types `Yes`
 *  or `NO` by hand round-trips too. Nothing else is guessed — `true`, `1` and
 *  `y` are refused rather than interpreted, because a wrong guess here decides
 *  whether a nurse appears on a requester picker. */
export function parseActiveCell(raw: string | undefined): boolean | undefined | 'refused' {
  if (raw === undefined) return undefined;
  const v = raw.trim().toLowerCase();
  if (v === '') return undefined;
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return 'refused';
}
